// File: frontend/src/context/UiLanguageContext.js
// Version: 1.0 (2026-09-28, Phase 9 — план 09-04)
// Description: язык интерфейса арендатора. Список языков и умолчание берутся ТОЛЬКО
// из признаков арендатора (`useBranding().features.ui_languages / ui_language_default`),
// а не из имени арендатора. У арендатора без признака (features пуст) языков нет:
// lang = null, переключатель не рисуется, t() отдаёт EN-словарь.
//
// 🔴 Хранилище выбора — localStorage под ключом `ui_lang:<instance>`. Всё обращение
//    к нему (включая САМО чтение свойства window.localStorage — в приватном режиме
//    Safari бросает уже оно) обёрнуто в try/catch: сбой хранилища не ломает экран.

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useBranding } from './BrandingContext';
import { perevesti as perevestiToday } from '../i18n/today';
import {
    perevesti as perevestiReputation,
    sklonit as sklonitReputation,
    SLOVAR as REPUTATION_SLOVAR,
} from '../i18n/reputation';
import {
    perevesti as perevestiCalendar,
    SLOVAR as CALENDAR_SLOVAR,
} from '../i18n/calendar';
import {
    perevesti as perevestiShell,
    sklonit as sklonitShell,
    SLOVAR as SHELL_SLOVAR,
} from '../i18n/shell';
import {
    perevesti as perevestiFirmToday,
    SLOVAR as FIRM_TODAY_SLOVAR,
} from '../i18n/firm-today';
import {
    perevesti as perevestiPractice,
    SLOVAR as PRACTICE_SLOVAR,
} from '../i18n/practice';
import {
    perevesti as perevestiAiops,
    SLOVAR as AIOPS_SLOVAR,
} from '../i18n/aiops';
import {
    perevesti as perevestiVoice,
    sklonit as sklonitVoice,
    SLOVAR as VOICE_SLOVAR,
} from '../i18n/voice';
import {
    perevesti as perevestiBlog,
    SLOVAR as BLOG_SLOVAR,
} from '../i18n/blog';

export function perevesti(lang, key, vars) {
    const yazyk = lang === 'ru' ? 'ru' : 'en';
    const voiceSlovarEn = VOICE_SLOVAR.en;
    if (Object.prototype.hasOwnProperty.call(voiceSlovarEn, key)) {
        return perevestiVoice(yazyk, key, vars);
    }
    if (Object.prototype.hasOwnProperty.call(BLOG_SLOVAR.en, key)) {
        return perevestiBlog(yazyk, key, vars);
    }
    const shellSlovarEn = SHELL_SLOVAR.en;
    if (Object.prototype.hasOwnProperty.call(shellSlovarEn, key)) {
        return perevestiShell(yazyk, key, vars);
    }
    const firmTodaySlovarEn = FIRM_TODAY_SLOVAR.en;
    if (Object.prototype.hasOwnProperty.call(firmTodaySlovarEn, key)) {
        return perevestiFirmToday(yazyk, key, vars);
    }
    const practiceSlovarEn = PRACTICE_SLOVAR.en;
    if (Object.prototype.hasOwnProperty.call(practiceSlovarEn, key)) {
        return perevestiPractice(yazyk, key, vars);
    }
    if (Object.prototype.hasOwnProperty.call(AIOPS_SLOVAR.en, key)) {
        return perevestiAiops(yazyk, key, vars);
    }
    const repSlovarEn = REPUTATION_SLOVAR.en;
    if (Object.prototype.hasOwnProperty.call(repSlovarEn, key)) {
        return perevestiReputation(yazyk, key, vars);
    }
    const calSlovarEn = CALENDAR_SLOVAR.en;
    if (Object.prototype.hasOwnProperty.call(calSlovarEn, key)) {
        return perevestiCalendar(yazyk, key, vars);
    }
    return perevestiToday(yazyk, key, vars);
}

export function sklonit(lang, baseKey, count, vars) {
    const yazyk = lang === 'ru' ? 'ru' : 'en';
    if (baseKey && baseKey.startsWith('voice.')) {
        return sklonitVoice(yazyk, baseKey, count, vars);
    }
    if (baseKey && baseKey.startsWith('shell.')) {
        return sklonitShell(yazyk, baseKey, count, vars);
    }
    return sklonitReputation(yazyk, baseKey, count, vars);
}

export const kluchYazyka = (inst) => `ui_lang:${inst || 'unknown'}`;

export function prochitatYazyk(inst) {
    try {
        const hranilishche = window.localStorage;
        if (!hranilishche) return null;
        return hranilishche.getItem(kluchYazyka(inst));
    } catch (e) {
        return null;
    }
}

export function zapisatYazyk(inst, yazyk) {
    try {
        const hranilishche = window.localStorage;
        if (!hranilishche) return false;
        hranilishche.setItem(kluchYazyka(inst), yazyk);
        return true;
    } catch (e) {
        return false;
    }
}

/** сохранённый ∈ ui_languages → он; иначе default ∈ ui_languages → он; иначе ui_languages[0]; иначе null. */
export function vybratYazyk(sohranyonnyy, features) {
    const f = features || {};
    const yazyki = Array.isArray(f.ui_languages) ? f.ui_languages : [];
    if (yazyki.length === 0) return null;
    if (sohranyonnyy && yazyki.includes(sohranyonnyy)) return sohranyonnyy;
    if (f.ui_language_default && yazyki.includes(f.ui_language_default)) return f.ui_language_default;
    return yazyki[0];
}

const NET_YAZYKOV = Object.freeze([]);
const NET_PRIZNAKOV = Object.freeze({});

export const UiLanguageContext = createContext({
    lang: null,
    setLang: () => {},
    languages: NET_YAZYKOV,
    t: (k, v) => perevesti(null, k, v),
    sklonit: (k, c, v) => sklonit(null, k, c, v),
});

export const useUiLanguage = () => useContext(UiLanguageContext);

export const UiLanguageProvider = ({ children }) => {
    const { branding, features: syryePriznaki } = useBranding();
    const features = syryePriznaki || NET_PRIZNAKOV;
    const inst = branding && branding.instance_name;
    const languages = Array.isArray(features.ui_languages) ? features.ui_languages : NET_YAZYKOV;

    const [lang, setLangState] = useState(null);

    useEffect(() => {
        setLangState(vybratYazyk(prochitatYazyk(inst), features));
    }, [inst, features]);

    const setLang = useCallback((v) => {
        if (!languages.includes(v)) return;
        setLangState(v);
        zapisatYazyk(inst, v);
    }, [languages, inst]);

    const umolchanie = features.ui_language_default;
    const t = useCallback(
        (key, vars) => perevesti(lang || umolchanie, key, vars),
        [lang, umolchanie],
    );
    const sklonitFn = useCallback(
        (baseKey, count, vars) => sklonit(lang || umolchanie, baseKey, count, vars),
        [lang, umolchanie],
    );

    const value = useMemo(
        () => ({ lang: lang || umolchanie || null, setLang, languages, t, sklonit: sklonitFn }),
        [lang, umolchanie, setLang, languages, t, sklonitFn],
    );

    return <UiLanguageContext.Provider value={value}>{children}</UiLanguageContext.Provider>;
};
